import type { AppConfig } from "../../types/appConfig";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";
import type {
  SMSCompanySettings,
  SMSPlaceholderDefinition,
  SMSProviderDefinition,
  SMSTemplate,
} from "./SMSTemplate.typed";

export class SMSTemplateModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async listPlaceholders(): Promise<SMSPlaceholderDefinition[]> {
    const r = await fetchZekay(this.zekayAPI + "/sms-templates/placeholders");
    await assertResponseOk(r);

    return (await r.json()) as SMSPlaceholderDefinition[];
  }

  async listProviders(): Promise<SMSProviderDefinition[]> {
    const r = await fetchZekay(this.zekayAPI + "/sms-providers");
    await assertResponseOk(r);

    return (await r.json()) as SMSProviderDefinition[];
  }

  async getSettings(): Promise<SMSCompanySettings> {
    const r = await fetchZekay(this.zekayAPI + "/sms-settings");
    await assertResponseOk(r);

    return (await r.json()) as SMSCompanySettings;
  }

  async updateSettings(payload: Partial<SMSCompanySettings>): Promise<SMSCompanySettings> {
    const r = await fetchZekay(this.zekayAPI + "/sms-settings", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(r);

    return (await r.json()) as SMSCompanySettings;
  }

  async listTemplates(): Promise<SMSTemplate[]> {
    const r = await fetchZekay(this.zekayAPI + "/sms-templates");
    await expectStatuses(r, 200, 204);
    if (r.status === 204) return [];

    return (await r.json()) as SMSTemplate[];
  }

  async createTemplate(payload: SMSTemplate): Promise<SMSTemplate> {
    const r = await fetchZekay(this.zekayAPI + "/sms-templates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(r, 201);

    return (await r.json()) as SMSTemplate;
  }

  async updateTemplate(id: number, payload: SMSTemplate): Promise<SMSTemplate | void> {
    const r = await fetchZekay(this.zekayAPI + "/sms-templates/" + id, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(r, 200, 204);
    if (r.status === 204) return;

    return (await r.json()) as SMSTemplate;
  }

  async deleteTemplate(id: number): Promise<void> {
    const r = await fetchZekay(this.zekayAPI + "/sms-templates/" + id, { method: "DELETE" });
    await expectStatuses(r, 204);
  }
}
